import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import {
  getCollectionsForSku,
  getPartitionsForSkuInCollection,
  OrderGuideSearchItemInfo,
} from '@/hooks/use-search-item-order-guide-info';
import {
  ItemListOptionMetadata,
  OrderGuideCollection,
  OrderGuidePartition,
} from '@/contexts/item-context-types';
import { ItemSubtitle } from '@/components/item-subtitle';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { UPDATE_ORDER_GUIDE_ACTION_SHEET_MAX_WIDTH } from '@/styles';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface SearchOrderGuideSelectionActionSheetProps {
  info: OrderGuideSearchItemInfo;
  onClose: () => void;
  onSelect: (sku: ItemListOptionMetadata, partitionUUID?: string) => void;
  renderCollectionRightComponent?: (
    collection: OrderGuideCollection,
    selectedSku: ItemListOptionMetadata,
  ) => React.ReactNode;
  renderPartitionRightComponent?: (
    partition: OrderGuidePartition,
    hasNotes: boolean,
    hasRequests: boolean,
  ) => React.ReactNode;
  skuSelectionOnly?: boolean;
  skusInOrderGuide: ItemListOptionMetadata[];
  title: string;
}

enum Route {
  SelectCollection = 'SELECT_COLLECTION',
  SelectPartition = 'SELECT_PARTITION',
  SelectSku = 'SELECT_SKU',
}

type SkuListOption = ListOption<{ sku: ItemListOptionMetadata }>;
type CollectionListOption = ListOption<{
  collection: OrderGuideCollection;
}>;
type PartitionListOption = ListOption<{
  hasNotes: boolean;
  hasRequests: boolean;
  partition: OrderGuidePartition;
}>;

export const SearchOrderGuideSelectionActionSheet = ({
  info,
  onClose,
  onSelect,
  renderCollectionRightComponent,
  renderPartitionRightComponent,
  skuSelectionOnly,
  skusInOrderGuide,
  title,
}: SearchOrderGuideSelectionActionSheetProps) => {
  const { currencyCode } = useVariantPackInfoContext();
  const { variantPackPartitionMap } = useOrderGuideItemsContext();
  const [styles] = useStyles();

  const [selectedSku, setSelectedSku] = useState<ItemListOptionMetadata | null>(
    skusInOrderGuide.length === 1 ? skusInOrderGuide[0] : null,
  );
  const { collections } = useOrderGuideItemsContext();

  const [selectedCollectionUUID, setSelectedCollectionUUID] = useState<
    string | null
  >(() => {
    if (skusInOrderGuide.length !== 1) {
      return null;
    }

    const cols = getCollectionsForSku(info, skusInOrderGuide[0], collections);
    return cols.length === 1 ? cols[0].uuid : null;
  });

  const collectionsForSku = useMemo(() => {
    if (!selectedSku) {
      return [];
    }

    return getCollectionsForSku(info, selectedSku, collections);
  }, [info, selectedSku, collections]);

  const partitionsForSkuInCollection = useMemo(() => {
    if (!selectedSku || !selectedCollectionUUID) {
      return [];
    }

    return getPartitionsForSkuInCollection(
      info,
      selectedSku,
      selectedCollectionUUID,
      collections,
    );
  }, [info, selectedSku, selectedCollectionUUID, collections]);

  // Auto-select when the remaining steps are already resolved
  useEffect(() => {
    if (!selectedSku) {
      return;
    }

    if (skuSelectionOnly) {
      onSelect(selectedSku);
      onClose();
      return;
    }

    if (selectedCollectionUUID && partitionsForSkuInCollection.length === 1) {
      onSelect(selectedSku, partitionsForSkuInCollection[0].partition.uuid);
      onClose();
    }
    // don't need this to re-run when the selected sku or collection UUID changes
    // since other handlers (handleSelectSku, handleSelectCollection, handleSelectPartition) will handle auto-selection afterwards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialRoute = useMemo(() => {
    if (skusInOrderGuide.length > 1) {
      return Route.SelectSku;
    }

    const sku = skusInOrderGuide[0];
    const collectionsForSku = getCollectionsForSku(info, sku, collections);
    if (collectionsForSku.length > 1) {
      return Route.SelectCollection;
    }

    return Route.SelectPartition;
  }, [info, skusInOrderGuide, collections]);

  // 1st action sheet shown: list of skus in _any_ order guide
  const skuListData = useMemo<SkuListOption[]>(() => {
    return skusInOrderGuide.map<SkuListOption>((sku) => ({
      metadata: { sku },
      subtitle: (
        <ItemSubtitle
          baseUnit={sku.baseUnit}
          currencyCode={currencyCode || DEFAULT_CURRENCY_CODE}
          displayString={sku.packDisplayString}
          modifierString={sku.modifierString}
          promotionPriceInMicros={sku.promotionPricePerUnitInMicros}
          showInlineModifiers={false}
          unitCount={sku.unitCount}
          unitPriceInMicros={sku.unitPriceInMicros}
        />
      ),
      title: sku.externalID || '',
      value: sku.variantPackUUID,
    }));
  }, [skusInOrderGuide, currencyCode]);

  // 2nd action sheet shown: list of collections for the selected sku
  const collectionListData = useMemo<CollectionListOption[]>(() => {
    return collectionsForSku.map<CollectionListOption>((collection) => ({
      metadata: { collection },
      title: collection.name,
      value: collection.uuid,
    }));
  }, [collectionsForSku]);

  // 3rd action sheet shown: list of partitions in the selected collection
  const partitionListData = useMemo<PartitionListOption[]>(() => {
    return partitionsForSkuInCollection.map<PartitionListOption>(
      ({ hasNotes, hasRequests, partition }) => ({
        metadata: { hasNotes, hasRequests, partition },
        title: partition.name,
        value: partition.uuid,
      }),
    );
  }, [partitionsForSkuInCollection]);

  const handleSelectSku = useCallback(
    (
      sku: ItemListOptionMetadata,
      jumpTo: (routeKey: Route) => void,
      close: () => void,
    ) => {
      if (skuSelectionOnly) {
        onSelect(sku);
        close();
        return;
      }

      const collectionsForSku = getCollectionsForSku(info, sku, collections);

      // Sku is in multiple collections: show collection action sheet (else select collection automatically)
      if (collectionsForSku.length > 1) {
        setSelectedSku(sku);
        jumpTo(Route.SelectCollection);
        return;
      }

      setSelectedCollectionUUID(collectionsForSku[0].uuid);

      // a sku is in only 1 collection, so all partitions it is in belong to that collection
      const skuPartitionUUIDs = Object.keys(
        variantPackPartitionMap[sku.variantPackUUID] || {},
      );

      if (skuPartitionUUIDs.length === 0) {
        return; // shouldn't happen unless OrderGuideSearchItemInfo or skusInOrderGuide props are incorrect
      }

      // Sku is in multiple partitions: show partition action sheet (else select partition automatically)
      if (skuPartitionUUIDs.length > 1) {
        setSelectedSku(sku);
        jumpTo(Route.SelectPartition);
        return;
      }

      onSelect(sku, skuPartitionUUIDs[0]);
      close();
    },
    [info, collections, onSelect, skuSelectionOnly, variantPackPartitionMap],
  );

  const handleSelectCollection = useCallback(
    (
      collection: OrderGuideCollection,
      jumpTo: (routeKey: Route) => void,
      close: () => void,
    ) => {
      if (!selectedSku) {
        return;
      }

      const partitionsForSkuInCollection = getPartitionsForSkuInCollection(
        info,
        selectedSku,
        collection.uuid,
        collections,
      );

      if (partitionsForSkuInCollection.length === 0) {
        return; // shouldn't happen unless OrderGuideSearchItemInfo or skusInOrderGuide props are incorrect
      }

      // Collection has multiple: show partition action sheet (else select partition automatically)
      if (partitionsForSkuInCollection.length > 1) {
        setSelectedCollectionUUID(collection.uuid);
        jumpTo(Route.SelectPartition);
        return;
      }

      // Collection has only 1 partition: select partition automatically
      onSelect(selectedSku, partitionsForSkuInCollection[0].partition.uuid);
      close();
    },
    [collections, info, selectedSku, onSelect],
  );

  const handleSelectPartition = useCallback(
    (partition: OrderGuidePartition, close: () => void) => {
      if (!selectedSku || !selectedCollectionUUID) {
        return;
      }

      onSelect(selectedSku, partition.uuid);
      close();
    },
    [selectedSku, selectedCollectionUUID, onSelect],
  );

  return (
    <CombinedActionSheet
      contentStyle={styles.content}
      initialRoute={initialRoute}
      modalStyle={styles.modalLarge}
      onClose={onClose}
      renderScene={({ routeKey, jumpTo, close }) => {
        switch (routeKey) {
          case Route.SelectSku:
            return (
              <List
                data={skuListData}
                onSelect={(sku) => {
                  if (!sku.metadata) {
                    return;
                  }

                  handleSelectSku(sku.metadata.sku, jumpTo, close);
                }}
              />
            );
          case Route.SelectCollection:
            return (
              <List
                data={collectionListData}
                onSelect={(collection) => {
                  if (!collection.metadata) {
                    return;
                  }

                  handleSelectCollection(
                    collection.metadata.collection,
                    jumpTo,
                    close,
                  );
                }}
                renderRightComponent={
                  renderCollectionRightComponent
                    ? (option) => {
                        if (!option.metadata || !selectedSku) {
                          return;
                        }

                        return renderCollectionRightComponent(
                          option.metadata.collection,
                          selectedSku,
                        );
                      }
                    : undefined
                }
              />
            );
          case Route.SelectPartition:
            return (
              <List
                data={partitionListData}
                onSelect={(partition) => {
                  if (!partition.metadata) {
                    return;
                  }

                  handleSelectPartition(partition.metadata.partition, close);
                }}
                renderRightComponent={
                  renderPartitionRightComponent
                    ? (option) => {
                        if (!option.metadata) {
                          return;
                        }

                        return renderPartitionRightComponent(
                          option.metadata.partition,
                          !!option.metadata.hasNotes,
                          !!option.metadata.hasRequests,
                        );
                      }
                    : undefined
                }
              />
            );
        }
      }}
      routes={{
        [Route.SelectSku]: {
          previousKey: null,
          showSmallLayoutTitle: true,
          subtitle: i18n.t(
            'search_order_guide_selection_action_sheet.item.title',
          ),
          title,
        },
        [Route.SelectCollection]: {
          previousKey: skusInOrderGuide.length > 1 ? Route.SelectSku : null,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          subtitle: i18n.t(
            'search_order_guide_selection_action_sheet.collections.title',
          ),
          title,
        },
        [Route.SelectPartition]: {
          previousKey:
            collectionsForSku.length > 1
              ? Route.SelectCollection
              : skusInOrderGuide.length > 1
                ? Route.SelectSku
                : null,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          subtitle: i18n.t(
            'search_order_guide_selection_action_sheet.partitions.title',
          ),
          title,
        },
      }}
      subtitleStyle={styles.modalSubtitle}
      titleStyle={styles.modalTitle}
    />
  );
};

const useStyles = createStyles(() => ({
  content: { paddingHorizontal: 0 },
  modalLarge: { maxWidth: UPDATE_ORDER_GUIDE_ACTION_SHEET_MAX_WIDTH },
  modalSubtitle: { marginLeft: 24 },
  modalTitle: { marginLeft: 24 },
}));
